import { UI_CONFIG_QUERY_KEY } from 'api/uiConfig/uiConfig.api'
import { clone, merge } from 'helpers/global.helper/global.helper'
import { queryClient } from 'helpers/queryClient'
import { applyPreviewEntry } from 'helpers/uiConfig.helper/uiConfig.helper'
import { computeUiConfigResolvedOutput } from 'helpers/uiConfig.helper/uiConfigResolutionPipeline.helper'
import authStore, { getActiveSystemFromLocalStorage } from 'store/auth/auth'
import { initializeTemplates } from 'ui/uiConfig/template/defaultUiConfig'
import { proxy, snapshot } from 'valtio'
import { subscribeKey } from 'valtio/utils'

type UiConfigEditorDefaultValues = {
  type?: string
  metadata_filter?: string
}

type UiConfigStore = {
  _activeSystemId: number | null
  editingUid?: UiConfigUid | null
  uiConfigs: UiConfigCollection
  activeUidsBySystemId: Record<number, UiConfigUid[]>
  previewCollection: UiConfigPreviewCollection
  versions: Record<UiConfigId, UiConfigUid[]>
  uiConfigEditorDefaultValues: UiConfigEditorDefaultValues
  baseUiConfig: UiConfig | null
  showDebugMenu: boolean
  nrOfUiConfigs: number
  editingUiConfig: UiConfig | null
  editingId: UiConfigId | null
  setPreviewDraft: (uid: UiConfigUid, draft: Partial<UiConfig>) => void
  setPreviewDraftPatch: (uid: UiConfigUid, draftPatch: Partial<UiConfig>) => void
  clearPreviewDraft: (uid: UiConfigUid) => void
  clearAllPreviewDrafts: () => void
  hasPreviewDraft: (uid: UiConfigUid) => boolean
  hasAnyPreviewDraft: () => boolean
  setEditingUid: (
    nextUid: UiConfigUid | null,
    options?: { confirmDiscardDraft?: (currentUid: UiConfigUid) => boolean }
  ) => boolean
  getParsedUiConfig: (
    uid: UiConfigUid,
    overrideAlias?: UiConfigAliases,
    options?: { overrideProps?: Record<string, unknown> }
  ) => UiConfig | null
  getUnparsedUiConfig: (uid: UiConfigUid, overrideAlias?: UiConfigAliases) => UiConfig | null
  getAnchorUiConfigByType: (type: string) => UiConfig | null
  activeUids: UiConfigUid[]
  idToUiConfig: UiConfigCollectionById
  editingUiConfigChildrenUids: Set<number>
  markHoursAhead: boolean
}

subscribeKey(authStore, `activeSystem`, (system) => {
  const uiConfigStoreSnap = snapshot(uiConfigStore)

  uiConfigStore._activeSystemId = system?.id ?? null
  uiConfigStore.editingUid = null

  // Remove all uiconfigs that are for another system.
  // Keep uiconfigs without systems as they are general and should be used for all systems.
  uiConfigStore.uiConfigs = Object.entries(uiConfigStoreSnap.uiConfigs)
    .filter(([_id, uiconfig]) => !uiconfig.system || uiconfig.system === system?.id)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

  queryClient.invalidateQueries({ queryKey: [UI_CONFIG_QUERY_KEY] })
})

const uiConfigStore = proxy<UiConfigStore>({
  _activeSystemId: getActiveSystemFromLocalStorage()?.id ?? null,
  editingUid: null,
  uiConfigs: {},
  activeUidsBySystemId: {},
  previewCollection: {},
  versions: {},
  uiConfigEditorDefaultValues: {},
  baseUiConfig: null,
  markHoursAhead: false,
  showDebugMenu: localStorage.getItem('uiConfig.showDebugMenu') === 'true',
  get activeUids() {
    return this.activeUidsBySystemId[this._activeSystemId ?? -1] ?? []
  },
  get nrOfUiConfigs() {
    return Object.keys(this.uiConfigs).length
  },
  get idToUiConfig() {
    return Object.values<UiConfig>(this.uiConfigs).reduce<UiConfigCollectionById>((acc, uiConfig) => {
      if (typeof uiConfig.id !== 'number') {
        return acc
      }

      acc[uiConfig.id] = uiConfig
      return acc
    }, {})
  },
  get editingUiConfig() {
    return this.getUnparsedUiConfig(this.editingUid ?? -1) ?? null
  },
  get editingId() {
    return this.getParsedUiConfig(this.editingUid ?? -1)?.id ?? null
  },
  setPreviewDraft(uid: UiConfigUid, draft: Partial<UiConfig>): void {
    // Explicitly carry all five editable keys (including undefined values) so
    // that the 'display_name' discriminator in applyPreviewEntry is present
    // and top-level field deletions are correctly handled when the preview is
    // applied via a top-level spread instead of a deep merge.
    const fullDraft: Partial<UiConfig> = {
      display_name: draft.display_name,
      props: draft.props,
      children_ids: draft.children_ids,
      dataset_instructions: draft.dataset_instructions,
      alias: draft.alias,
    }

    if (!hasMeaningfulDraftContent(fullDraft)) {
      delete this.previewCollection[uid]
      return
    }

    this.previewCollection[uid] = fullDraft
  },
  setPreviewDraftPatch(uid: UiConfigUid, draftPatch: Partial<UiConfig>): void {
    const currentDraft = this.previewCollection?.[uid] || {}
    const mergedDraft = mergeDraftPatch(currentDraft, draftPatch)

    if (!hasMeaningfulDraftContent(mergedDraft)) {
      delete this.previewCollection[uid]
      return
    }

    this.previewCollection[uid] = mergedDraft
  },
  clearPreviewDraft(uid: UiConfigUid): void {
    delete this.previewCollection[uid]
  },
  clearAllPreviewDrafts(): void {
    this.previewCollection = {}
  },
  hasPreviewDraft(uid: UiConfigUid): boolean {
    return hasMeaningfulDraftContent(this.previewCollection?.[uid])
  },
  hasAnyPreviewDraft(): boolean {
    return Object.values(this.previewCollection).some((entry) => hasMeaningfulDraftContent(entry))
  },
  setEditingUid(
    nextUid: UiConfigUid | null,
    options: { confirmDiscardDraft?: (currentUid: UiConfigUid) => boolean } = {}
  ): boolean {
    const currentUid = this.editingUid

    if (
      typeof currentUid === 'number' &&
      typeof nextUid === 'number' &&
      currentUid !== nextUid &&
      this.hasPreviewDraft(currentUid)
    ) {
      const shouldDiscardDraft = options.confirmDiscardDraft ? options.confirmDiscardDraft(currentUid) : true

      if (!shouldDiscardDraft) {
        return false
      }

      this.clearPreviewDraft(currentUid)
    }

    this.editingUid = nextUid
    return true
  },
  get getParsedUiConfig() {
    return (
      uid: UiConfigUid,
      overrideAlias?: UiConfigAliases,
      options: { overrideProps?: Record<string, unknown> } = {}
    ): UiConfig | null => {
      const uiConfig = this.uiConfigs[uid]

      if (!uiConfig) {
        return null
      }

      const finalUiConfig = computeUiConfigResolvedOutput({
        rawUiConfig: uiConfig,
        baseUiConfig: this.baseUiConfig,
        previewCollection: this.previewCollection,
        overrideAlias,
        overrideProps: options.overrideProps,
      })

      return finalUiConfig
    }
  },
  get getUnparsedUiConfig() {
    return (uid: UiConfigUid) => {
      const uiConfig = this.uiConfigs[uid]

      if (!uiConfig) {
        return null
      }

      const previewedUiConfig = typeof uiConfig.uid === 'number' ? this.previewCollection?.[uiConfig.uid] : undefined

      if (!previewedUiConfig) {
        return clone(uiConfig)
      }

      return applyPreviewEntry(uiConfig, previewedUiConfig)
    }
  },
  get getAnchorUiConfigByType() {
    return (type: string) => {
      const uiconfig = Object.values<UiConfig>(this.uiConfigs).find((candidate) => candidate.component === type)

      if (!uiconfig) {
        return null
      }

      return this.getParsedUiConfig(uiconfig.uid)
    }
  },
  get editingUiConfigChildrenUids() {
    const childrenUids: Set<number> = new Set([])
    const uid = this.editingUid
    if (!uid) {
      return childrenUids
    }

    const getChildrenUids = (parentUid: number): Set<number> => {
      const parent = this.uiConfigs[parentUid]
      const uids: Set<number> = new Set([parentUid])

      if (!parent) {
        return uids
      }

      parent.children_ids?.forEach((childUid) => {
        const grandChildrenUids = getChildrenUids(childUid)

        grandChildrenUids.forEach((grandChildUid) => {
          uids.add(grandChildUid)
        })
      })

      return uids
    }

    return getChildrenUids(uid)
  },
})

subscribeKey(uiConfigStore, 'showDebugMenu', (showDebugMenu) => {
  localStorage.setItem('uiConfig.showDebugMenu', JSON.stringify(showDebugMenu))
})

export function addUiConfig(uiConfig: UiConfig, autoFocus = true): void {
  const snap = snapshot(uiConfigStore)

  if (!uiConfig.uid) {
    return
  }

  uiConfigStore.uiConfigs = {
    ...snap.uiConfigs,
    [uiConfig.uid]: uiConfig,
  }

  if (autoFocus) {
    uiConfigStore.editingUid = uiConfig.uid
  }

  if (uiConfig.component === `base_config`) {
    uiConfigStore.baseUiConfig = uiConfig
  }
}

export function addUiConfigs(newUiConfigs: UiConfig[]): void {
  const snap = snapshot(uiConfigStore)

  // By UID
  const uiConfigsById = newUiConfigs.reduce((acc, uiConfig) => {
    if (!uiConfig.uid) {
      return acc
    }

    return {
      ...acc,
      [uiConfig.uid]: uiConfig,
    }
  }, {} as UiConfigCollection)
  const uiConfigCollection = {
    ...snap.uiConfigs,
    ...uiConfigsById,
  }
  uiConfigStore.uiConfigs = uiConfigCollection

  // Get uids for highest versions (only 1 uid for every id)
  const idToHighestVersionAndUidMap: Record<number, { version?: number; uid?: number }> = {}
  Object.values(uiConfigCollection).forEach((uiConfig) => {
    if ((idToHighestVersionAndUidMap[uiConfig.id as number]?.version ?? -1) < (uiConfig.version as number)) {
      idToHighestVersionAndUidMap[uiConfig.id as number] = {
        version: uiConfig.version,
        uid: uiConfig.uid,
      }
    }
  })
  const uidsWithHighestVersions: Set<number | undefined> = new Set(
    Object.entries(idToHighestVersionAndUidMap).map(([, { uid }]) => uid)
  )

  // By system
  const newActiveUidsBySystemId = Object.values<UiConfig>(uiConfigCollection).reduce(
    (acc, uiConfig) => ({
      ...acc,
      [uiConfig.system ?? -1]: [...(acc[uiConfig.system ?? -1] ?? []), uiConfig.uid as number].filter((uid) =>
        uidsWithHighestVersions.has(uid)
      ),
    }),
    {} as Record<number, number[]>
  )
  const activeUidsBySystemId = {
    ...snap.activeUidsBySystemId,
    ...newActiveUidsBySystemId,
  }
  uiConfigStore.activeUidsBySystemId = activeUidsBySystemId

  // By version
  const versions: Record<UiConfigId, UiConfigUid[]> = {}
  Object.values(uiConfigCollection).forEach((uiConfig) => {
    if (!uiConfig.id) {
      return
    }

    if (!versions[uiConfig.id]) {
      versions[uiConfig.id] = []
    }

    if (uiConfig.uid) {
      versions[uiConfig.id].unshift(uiConfig.uid)
    }
  })
  uiConfigStore.versions = {
    ...snap.versions,
    ...versions,
  }

  newUiConfigs.forEach((uiConfig) => {
    if (uiConfig.component === `base_config`) {
      uiConfigStore.baseUiConfig = uiConfig
    }
  })
}

export function removeUiConfigs(id: UiConfigId): void {
  const uiConfigSnap = snapshot(uiConfigStore)
  const removeUids: Set<UiConfigUid> = new Set()
  uiConfigStore.editingUid = null
  delete uiConfigStore.versions[id]
  Object.entries(uiConfigSnap.uiConfigs).forEach(([uid, uiConfig]) => {
    if (uiConfig.id === id) {
      delete uiConfigStore.uiConfigs[uid]
      removeUids.add(Number(uid))
    }
  })
  Object.entries(uiConfigSnap.previewCollection).forEach(([uid]) => {
    const uidAsNumber = Number(uid)
    if (removeUids.has(uidAsNumber)) {
      delete uiConfigStore.previewCollection[uidAsNumber]
    }
  })
  Object.entries(uiConfigSnap.activeUidsBySystemId).forEach(([systemId, uids]: any) => {
    uiConfigStore.activeUidsBySystemId[systemId] = uids.filter((uid: any) => !removeUids.has(uid))
  })
}

initializeTemplates((uiConfig: UiConfig) => addUiConfig(uiConfig, false))
export default uiConfigStore

function hasMeaningfulDraftContent(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.length > 0
  }

  if (value && typeof value === 'object') {
    const values = Object.values(value)

    if (values.length === 0) {
      return false
    }

    return values.some((entry) => hasMeaningfulDraftContent(entry))
  }

  return value !== undefined
}

function mergeDraftPatch(base: Partial<UiConfig>, patch: Partial<UiConfig>): Partial<UiConfig> {
  // Do not clone via JSON.parse/stringify here — that would drop keys whose value
  // is `undefined`, destroying the deletion markers that distinguish a full editor
  // draft (has `display_name` key) from a partial preview.  The `merge()` helper
  // already returns a new object, so no defensive clone is needed.
  return merge(base, patch) as Partial<UiConfig>
}
